import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import IntroScreen from './components/IntroScreen';
import ThankYouScreen from './components/ThankYouScreen';
import ConfirmationScreen from './components/ConfirmationScreen';
import EventDetails from './components/EventDetails';
import DressCodeModal from './components/DressCodeModal';
import CancellationModal from './components/CancellationModal';
import RSVPForm from './components/RSVPForm';
import MusicToggle from './components/MusicToggle';

// Foto di fondo della schermata invito: metti il file in client/public/
const INVITE_PHOTO = process.env.PUBLIC_URL + '/tavola.jpg';
// Musica di sottofondo: parte all'apertura della busta
const INVITE_MUSIC = process.env.PUBLIC_URL + '/traviata2.mp3';

// Luogo della festa: https://maps.app.goo.gl/jzcuVterY5rpmKS67
// Le coordinate evitano che Maps risolva l'indirizzo su un punto sbagliato
const VENUE_NAME = 'MAX | Massimo Mentasti';
const VENUE_COORDS = '40.1240686,18.3027678';
const VENUE_MAPS_LINK = 'https://maps.app.goo.gl/jzcuVterY5rpmKS67';

function App() {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    plusOne: false,
    nomePlusOne: '',
    cognomePlusOne: '',
    intolleranze: false,
    tipoIntolleranza: [],
    altroIntolleranza: '',
    intolleranzePlusOne: false,
    tipoIntolleranzaPlusOne: [],
    altroIntolleranzaPlusOne: '',
    preferenzeAlimentari: false,
    tipoPreferenzaAlimentare: [],
    altroPreferenzaAlimentare: '',
    preferenzeAlimentariPlusOne: false,
    tipoPreferenzaAlimentarePlusOne: [],
    altroPreferenzaAlimentarePlusOne: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showIntro, setShowIntro] = useState(true);
  const [envelopeOpening, setEnvelopeOpening] = useState(false);
  const [showDressCodeModal, setShowDressCodeModal] = useState(false);
  const [alreadyConfirmed] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationData, setCancellationData] = useState({
    nome: '',
    cognome: '',
    hasPlusOne: false,
    nomePlusOne: '',
    cognomePlusOne: ''
  });
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [cancellationError, setCancellationError] = useState('');
  const [cancellationSubmitted, setCancellationSubmitted] = useState(false);
  const audioRef = useRef(null);
  const [musicStarted, setMusicStarted] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (showDressCodeModal || showCancellationModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDressCodeModal, showCancellationModal]);

  const playMusic = () => {
    const playPromise = audioRef.current.play();
    if (playPromise) {
      playPromise.then(() => setMusicPlaying(true)).catch(() => setMusicPlaying(false));
    } else {
      setMusicPlaying(true);
    }
  };

  const toggleMusic = () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      playMusic();
    } else {
      audioRef.current.pause();
      setMusicPlaying(false);
    }
  };

  const handleEnvelopeClick = () => {
    // Il click è il gesto utente che autorizza l'audio nei browser
    if (!audioRef.current) {
      const audio = new Audio(INVITE_MUSIC);
      audio.loop = true;
      audio.volume = 0.6;
      audioRef.current = audio;
    }
    setMusicStarted(true);
    playMusic();

    setEnvelopeOpening(true);
    // L'animazione del biglietto dura 2s: lo lasciamo a schermo altri 2.5s
    setTimeout(() => {
      setShowIntro(false);
    }, 4500);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'intolleranze' && !checked) {
        newData.tipoIntolleranza = [];
        newData.altroIntolleranza = '';
      }

      if (name === 'intolleranzePlusOne' && !checked) {
        newData.tipoIntolleranzaPlusOne = [];
        newData.altroIntolleranzaPlusOne = '';
      }

      if (name === 'preferenzeAlimentari' && !checked) {
        newData.tipoPreferenzaAlimentare = [];
        newData.altroPreferenzaAlimentare = '';
      }

      if (name === 'preferenzeAlimentariPlusOne' && !checked) {
        newData.tipoPreferenzaAlimentarePlusOne = [];
        newData.altroPreferenzaAlimentarePlusOne = '';
      }

      return newData;
    });
  };

  const handleIntolleranzaChange = (value) => {
    setFormData(prev => {
      const currentIntolleranze = prev.tipoIntolleranza;
      const newIntolleranze = currentIntolleranze.includes(value)
        ? currentIntolleranze.filter(i => i !== value)
        : [...currentIntolleranze, value];
      return {
        ...prev,
        tipoIntolleranza: newIntolleranze
      };
    });
  };

  const handleIntolleranzaPlusOneChange = (value) => {
    setFormData(prev => {
      const currentIntolleranze = prev.tipoIntolleranzaPlusOne;
      const newIntolleranze = currentIntolleranze.includes(value)
        ? currentIntolleranze.filter(i => i !== value)
        : [...currentIntolleranze, value];
      return {
        ...prev,
        tipoIntolleranzaPlusOne: newIntolleranze
      };
    });
  };

  const handlePreferenzaAlimentareChange = (value) => {
    setFormData(prev => {
      const current = prev.tipoPreferenzaAlimentare;
      const updated = current.includes(value)
        ? current.filter(i => i !== value)
        : [...current, value];
      return {
        ...prev,
        tipoPreferenzaAlimentare: updated,
        altroPreferenzaAlimentare: updated.includes('altro') ? prev.altroPreferenzaAlimentare : ''
      };
    });
  };

  const handlePreferenzaAlimentarePlusOneChange = (value) => {
    setFormData(prev => {
      const current = prev.tipoPreferenzaAlimentarePlusOne;
      const updated = current.includes(value)
        ? current.filter(i => i !== value)
        : [...current, value];
      return {
        ...prev,
        tipoPreferenzaAlimentarePlusOne: updated,
        altroPreferenzaAlimentarePlusOne: updated.includes('altro') ? prev.altroPreferenzaAlimentarePlusOne : ''
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading || submitted) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Errore durante l\'invio');
      }
    } catch (err) {
      setError('Errore di connessione. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const addToCalendar = () => {
    const eventTitle = 'Compleanno 30 anni Gianluca';
    const eventDetails = 'Festa di compleanno';
    const eventLocation = 'Max Massimo Mentasti, Via de Viti de Marco, S/N, 73024 Maglie';

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Compleanno Gianluca//IT',
      'BEGIN:VEVENT',
      'DTSTART:20261114T200000',
      'DTEND:20261115T020000',
      `SUMMARY:${eventTitle}`,
      `DESCRIPTION:${eventDetails}`,
      `LOCATION:${eventLocation}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openMaps = () => {
    const encoded = encodeURIComponent(VENUE_NAME);

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    if (isIOS) {
      // Se Google Maps è installata, la pagina va in background (hidden).
      // Se dopo 500ms è ancora visibile, Google Maps non c'è → Apple Maps.
      let appOpened = false;

      const handleVisibilityChange = () => {
        if (document.hidden) appOpened = true;
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.location.href = `comgooglemaps://?q=${VENUE_COORDS}&center=${VENUE_COORDS}&zoom=17`;

      setTimeout(() => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (!appOpened) {
          window.location.href = `maps://?q=${encoded}&ll=${VENUE_COORDS}`;
        }
      }, 500);
    } else if (isAndroid) {
      window.open(`geo:${VENUE_COORDS}?q=${VENUE_COORDS}(${encoded})`, '_blank');
    } else {
      window.open(VENUE_MAPS_LINK, '_blank');
    }
  };

  const openDressCodeModal = () => {
    setShowDressCodeModal(true);
  };

  const closeDressCodeModal = () => {
    setShowDressCodeModal(false);
  };

  const openCancellationModal = () => {
    setShowCancellationModal(true);
    setCancellationError('');
  };

  const closeCancellationModal = () => {
    setShowCancellationModal(false);
    setCancellationData({
      nome: '',
      cognome: '',
      hasPlusOne: false,
      nomePlusOne: '',
      cognomePlusOne: ''
    });
    setCancellationError('');
  };

  const handleCancellationChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCancellationData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCancellationSubmit = async (e) => {
    e.preventDefault();

    if (cancellationLoading) return;

    setCancellationLoading(true);
    setCancellationError('');

    try {
      const response = await fetch('/api/cancel-rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cancellationData),
      });

      const data = await response.json();

      if (response.ok) {
        setCancellationSubmitted(true);
        setTimeout(() => {
          closeCancellationModal();
          setCancellationSubmitted(false);
        }, 3000);
      } else {
        setCancellationError(data.error || 'Errore durante l\'invio');
      }
    } catch (err) {
      setCancellationError('Errore di connessione. Riprova più tardi.');
    } finally {
      setCancellationLoading(false);
    }
  };

  if (Date.now() >= new Date('2026-11-14T21:00:00').getTime()) {
    return (
      <ThankYouScreen />
    );
  }

  if (showIntro) {
    return (
      <IntroScreen envelopeOpening={envelopeOpening} onEnvelopeClick={handleEnvelopeClick} />
    );
  }

  if (submitted) {
    return (
      <>
        <ConfirmationScreen
          alreadyConfirmed={alreadyConfirmed}
          showDressCodeModal={showDressCodeModal}
          onOpenMaps={openMaps}
          onAddToCalendar={addToCalendar}
          onOpenDressCode={openDressCodeModal}
          onCloseDressCode={closeDressCodeModal}
        />
        {musicStarted && (
          <MusicToggle isPlaying={musicPlaying} onToggle={toggleMusic} />
        )}
      </>
    );
  }

  return (
    <div className="App invite-screen">
      <div className="invite-bg" aria-hidden="true" style={{ backgroundImage: `url(${INVITE_PHOTO})` }}></div>
      <div className="invite-frame" aria-hidden="true"></div>

      <div className="invitation-card">
        <div className="invite-hero">
          <div className="hero-content">
            <div className="hero-top">
              <div className="hero-note-block">
                <span className="hero-note">Join us for a<br />special dinner<br />to celebrate</span>
                <span className="hero-rule"></span>
              </div>
            </div>

            <div className="hero-body">
              <p className="hero-script">Gianluca's</p>
              <p className="hero-number">30<span className="hero-th">th</span></p>
              <p className="hero-birthday">Birthday</p>

              <p className="hero-tagline">
                Libiamo<br />
                Ne'<br />
                Lieti Calici
              </p>
              <span className="hero-rule"></span>

              <EventDetails
                onOpenMaps={openMaps}
                onAddToCalendar={addToCalendar}
              />

              <span className="hero-rule"></span>
              <p className="hero-celebrate">Let's celebrate!</p>
              <span className="hero-rule"></span>
              
            </div>
          </div>
        </div>

        <div className="invite-below">
          <div className="invite-below-inner">
            <div className="cancellation-banner" onClick={openCancellationModal}>
              <span className="banner-icon">⚠️</span>
              <span className="banner-text">Hai dato conferma e non puoi più venire?</span>
              <span className="banner-arrow">›</span>
            </div>

            <div className="divider"></div>

            <RSVPForm
              formData={formData}
              onChange={handleChange}
              onSubmit={handleSubmit}
              onIntolleranzaChange={handleIntolleranzaChange}
              onIntolleranzaPlusOneChange={handleIntolleranzaPlusOneChange}
              onPreferenzaAlimentareChange={handlePreferenzaAlimentareChange}
              onPreferenzaAlimentarePlusOneChange={handlePreferenzaAlimentarePlusOneChange}
              loading={loading}
              error={error}
            />

            <div className="ornament-bottom"></div>
          </div>
        </div>
      </div>

      <CancellationModal
        isOpen={showCancellationModal}
        onClose={closeCancellationModal}
        cancellationData={cancellationData}
        onDataChange={handleCancellationChange}
        onSubmit={handleCancellationSubmit}
        isLoading={cancellationLoading}
        error={cancellationError}
        isSubmitted={cancellationSubmitted}
      />

      {musicStarted && (
        <MusicToggle isPlaying={musicPlaying} onToggle={toggleMusic} />
      )}
    </div>
  );
}

export default App;
